import { useState } from 'react'
import { api, useAbruf, type ApiFehler } from '../api'
import { Angabe, Fehleranzeige, Karte, Merkmal, Tabelle, Verlauf, Zustand } from '../bausteine'
import { datum, menge, prozent, zahl, zeitpunkt } from '../formate'

interface Prognoseblatt {
  tag: string
  zeitscheiben: number
  tagesmengeKwh: string
  vertraege: number
  ausProfil: number
  ausLastgang: number
  guete: string
  unsichere: string[]
  werte: string[]
}

interface Meldungsblatt {
  id: string
  bilanzkreisEic: string
  tag: string
  version: number
  tagesmengeKwh: string
  zeitscheiben: number
  angenommen: boolean
  vorgangskennung: string | null
  beanstandungen: string | null
  gemeldetAm: string
}

interface Preiszeile {
  produkt: string
  periode: string
  preisEurProMwh: string
  handelstag: string
}

interface Positionsblatt {
  periode: string
  bedarfMwh: string
  kontrahiertMwh: string
  offenMwh: string
  einstandEurProMwh: string
  deckungsgrad: string
  unterdeckt: boolean
}

export default function Bilanzierung() {
  const morgen = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)
  const [tag, setzeTag] = useState(morgen)
  const prognose = useAbruf<Prognoseblatt>(`/api/bilanzierung/prognose?tag=${tag}`, [tag])
  const preise = useAbruf<Preiszeile[]>('/api/bilanzierung/beschaffung/terminpreise')

  const [meldung, setzeMeldung] = useState<Meldungsblatt>()
  const [fehler, setzeFehler] = useState<ApiFehler>()
  const [laeuft, setzeLaeuft] = useState(false)

  const [jahr, setzeJahr] = useState(new Date().getFullYear() + 1)
  const [monat, setzeMonat] = useState('')
  const [position, setzePosition] = useState<Positionsblatt>()
  const [positionsfehler, setzePositionsfehler] = useState<ApiFehler>()
  const [positionLaeuft, setzePositionLaeuft] = useState(false)

  async function anmelden() {
    setzeFehler(undefined)
    setzeLaeuft(true)
    try {
      setzeMeldung(await api.sende<Meldungsblatt>('/api/bilanzierung/fahrplaene', { tag }))
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    } finally {
      setzeLaeuft(false)
    }
  }

  async function positionErmitteln() {
    setzePositionsfehler(undefined)
    setzePosition(undefined)
    setzePositionLaeuft(true)
    try {
      const pfad = monat
        ? `/api/bilanzierung/beschaffung/position?jahr=${jahr}&monat=${monat}`
        : `/api/bilanzierung/beschaffung/position?jahr=${jahr}`
      setzePosition(await api.lies<Positionsblatt>(pfad))
    } catch (ursache) {
      setzePositionsfehler(ursache as ApiFehler)
    } finally {
      setzePositionLaeuft(false)
    }
  }

  return (
    <>
      <h1>Bilanzierung und Beschaffung</h1>
      <p className="untertitel">
        Was die Kunden entnehmen, muss beschafft und in Fahrplänen angemeldet sein. Die
        Abweichung wird als Ausgleichsenergie abgerechnet.
      </p>

      <Karte titel="Absatzprognose">
        <div className="leiste">
          <div className="feld">
            <label htmlFor="tag">Liefertag</label>
            <input
              id="tag"
              type="date"
              value={tag}
              onChange={(ereignis) => setzeTag(ereignis.target.value)}
            />
          </div>
          <button className="betont" disabled={laeuft} onClick={anmelden}>
            {laeuft ? 'Meldet an …' : 'Fahrplan anmelden'}
          </button>
        </div>

        {fehler && <Fehleranzeige fehler={fehler} />}
        {meldung && (
          <div className={`hinweis ${meldung.angenommen ? 'info' : 'fehler'}`}>
            <strong>
              Version {meldung.version} für {meldung.bilanzkreisEic},{' '}
              {meldung.angenommen ? 'angenommen' : 'zurückgewiesen'}
            </strong>
            <div>
              {menge(meldung.tagesmengeKwh)} in {meldung.zeitscheiben} Zeitscheiben, gemeldet{' '}
              {zeitpunkt(meldung.gemeldetAm)}
            </div>
            {meldung.beanstandungen && <div>{meldung.beanstandungen}</div>}
          </div>
        )}

        <Zustand laeuft={prognose.laeuft} fehler={prognose.fehler}>
          {prognose.daten && (
            <>
              <div className="angaben" style={{ marginBottom: 16 }}>
                <Angabe beschriftung="Tagesmenge">
                  {menge(prognose.daten.tagesmengeKwh)}
                </Angabe>
                <Angabe beschriftung="Zeitscheiben">
                  {prognose.daten.zeitscheiben}
                  {prognose.daten.zeitscheiben !== 96 && (
                    <>
                      {' '}
                      <Merkmal art="warnung">Zeitumstellung</Merkmal>
                    </>
                  )}
                </Angabe>
                <Angabe beschriftung="Verträge">{prognose.daten.vertraege}</Angabe>
                <Angabe beschriftung="Aus Profil">{prognose.daten.ausProfil}</Angabe>
                <Angabe beschriftung="Aus Lastgang">{prognose.daten.ausLastgang}</Angabe>
                <Angabe beschriftung="Güte">
                  {prozent(Number(prognose.daten.guete) * 100)}
                </Angabe>
              </div>

              {prognose.daten.unsichere.length > 0 && (
                <div className="hinweis warnung">
                  {prognose.daten.unsichere.length} Verträge gehen gleichverteilt ein, weil
                  weder Profil noch Lastgang vorliegt.
                </div>
              )}

              <Verlauf
                werte={prognose.daten.werte.map(Number)}
                beschriftungLinks="00:00"
                beschriftungRechts="24:00"
              />
            </>
          )}
        </Zustand>
      </Karte>

      <Karte titel="Offene Beschaffungsposition">
        <div className="leiste">
          <div className="feld">
            <label htmlFor="jahr">Lieferjahr</label>
            <input
              id="jahr"
              type="number"
              style={{ width: 110 }}
              value={jahr}
              onChange={(ereignis) => setzeJahr(Number(ereignis.target.value))}
            />
          </div>
          <div className="feld">
            <label htmlFor="monat">Monat (leer = Jahresband)</label>
            <select
              id="monat"
              value={monat}
              onChange={(ereignis) => setzeMonat(ereignis.target.value)}
            >
              <option value="">Ganzes Jahr</option>
              {Array.from({ length: 12 }, (_, stelle) => stelle + 1).map((wert) => (
                <option key={wert} value={wert}>
                  {wert}
                </option>
              ))}
            </select>
          </div>
          <button disabled={positionLaeuft} onClick={positionErmitteln}>
            {positionLaeuft ? 'Rechnet …' : 'Position ermitteln'}
          </button>
        </div>
        {positionsfehler && <Fehleranzeige fehler={positionsfehler} />}
        {position && (
          <div className="angaben">
            <Angabe beschriftung="Periode">{position.periode}</Angabe>
            <Angabe beschriftung="Bedarf">{menge(position.bedarfMwh, 'MWh')}</Angabe>
            <Angabe beschriftung="Kontrahiert">
              {menge(position.kontrahiertMwh, 'MWh')}
            </Angabe>
            <Angabe beschriftung="Offen">
              {menge(position.offenMwh, 'MWh')}{' '}
              {position.unterdeckt && <Merkmal art="warnung">Unterdeckt</Merkmal>}
            </Angabe>
            <Angabe beschriftung="Deckungsgrad">
              {prozent(Number(position.deckungsgrad) * 100)}
            </Angabe>
            <Angabe beschriftung="Einstand">
              {zahl(position.einstandEurProMwh, 2)} EUR/MWh
            </Angabe>
          </div>
        )}
      </Karte>

      <Karte titel="Terminpreise">
        <Zustand
          laeuft={preise.laeuft}
          fehler={preise.fehler}
          leer={(preise.daten ?? []).length === 0}
        >
          <Tabelle
            spalten={[
              { name: 'Band' },
              { name: 'Periode' },
              { name: 'Preis', zahl: true },
              { name: 'Handelstag' },
            ]}
          >
            {(preise.daten ?? [])
              .filter((preis) => preis.periode.startsWith('Cal') || preis.periode.startsWith('Q'))
              .map((preis) => (
                <tr key={`${preis.produkt}-${preis.periode}`}>
                  <td>{preis.produkt}</td>
                  <td>{preis.periode}</td>
                  <td className="zahl">{zahl(preis.preisEurProMwh, 2)} EUR/MWh</td>
                  <td>{datum(preis.handelstag)}</td>
                </tr>
              ))}
          </Tabelle>
        </Zustand>
      </Karte>
    </>
  )
}
